import React, { useRef, useState } from 'react';
import { createStructuredSelector } from 'reselect';
import { RootState } from '../PanesPanel/types';
import { useDispatch, useSelector } from 'react-redux';
import { FiChevronDown } from 'react-icons/all';
import { useTranslation } from 'react-i18next';
import { ActivePanel } from '../Configurator/constants/general';
import {
  makeSelectActivePanel,
  makeSelectContext,
} from '../Configurator/selectors/general';
import Img from '../../components/Img';
import platesImg from 'assets/images/ribbons/shelvesRibbon/Plates.svg';
import drawersImg from 'assets/images/ribbons/shelvesRibbon/drawers.svg';
import ISImg from 'assets/images/ribbons/shelvesRibbon/IS.svg';
import panesImg from 'assets/images/ribbons/shelvesRibbon/panes.svg';
import handlesImg from 'assets/images/ribbons/shelvesRibbon/handlesSelected.svg';
import F1Img from 'assets/images/cross_pieces/F1.png';
import { setColorsPanelAction } from '../Configurator/actions/general';
import useOnClickOutside from 'use-onclickoutside';
import {
  makeSelectHaveOneDoor,
  makeSelectHaveOneDrawer,
} from '../Ribbon/ShelvesRibbon/selectors';
import { makeSelectCanAddIs } from '../Ribbon/ShelvesRibbon/ColorsSection/selectors';
import { makeSelectFullDrawersSelection } from '../DrawersPanel/selectors';
import { FurnitureEditorContext } from '../../lib/constraintsChecker/FurnitureEditor';

// import styled from 'styled-components';

interface StateProps {
  activePanel: ActivePanel | undefined;
  haveOneDrawer: boolean;
  haveOneDoor: boolean;
  isAllSelectionDrawers: boolean;
  canAddIs: boolean;
  context: FurnitureEditorContext;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  activePanel: makeSelectActivePanel(),
  haveOneDrawer: makeSelectHaveOneDrawer(),
  haveOneDoor: makeSelectHaveOneDoor(),
  isAllSelectionDrawers: makeSelectFullDrawersSelection(),
  canAddIs: makeSelectCanAddIs(),
  context: makeSelectContext(),
});

const PanelSwitcher = () => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const ref = useRef(null);
  useOnClickOutside(ref, () => {
    setOpen(false);
  });

  const {
    activePanel,
    haveOneDrawer,
    haveOneDoor,
    isAllSelectionDrawers,
    canAddIs,
    context,
  } = useSelector(stateSelector);

  function getTitle(panel: ActivePanel): string {
    switch (panel) {
      case ActivePanel.handles:
        return t('handles');
      case ActivePanel.drawers:
        return t('drawers');
      case ActivePanel.plates:
        return t('plates');
      case ActivePanel.panes:
        return t('doors/sides/backpanes');
      case ActivePanel.IntermediateShelf:
        return t('intermediate shelves');
      case ActivePanel.crossPieces:
        return t('crossPieces');
      default:
        return panel;
    }
  }

  function switchPanel(panel: ActivePanel) {
    dispatch(setColorsPanelAction(panel));
    setOpen(false);
  }

  function filterPanel(panel: ActivePanel) {
    if (panel === activePanel) {
      return false;
    } else if (panel === ActivePanel.drawers) {
      return haveOneDrawer;
    } else if (panel === ActivePanel.handles) {
      return haveOneDoor || haveOneDrawer;
    } else if (panel === ActivePanel.IntermediateShelf) {
      return false;
      // return !isAllSelectionDrawers && canAddIs;
    } else if (panel === ActivePanel.crossPieces) {
      if (process.env.NODE_ENV !== 'production') return true;
      return context === FurnitureEditorContext.sales;
    } else if (
      panel === ActivePanel.paint ||
      panel === ActivePanel.upperShelf
    ) {
      return false;
    }

    return true;
  }

  function getImg(panel: ActivePanel) {
    switch (panel) {
      case ActivePanel.drawers:
        return drawersImg;
      case ActivePanel.panes:
        return panesImg;
      case ActivePanel.plates:
        return platesImg;
      case ActivePanel.handles:
        return handlesImg;
      case ActivePanel.IntermediateShelf:
        return ISImg;
      case ActivePanel.crossPieces:
        return F1Img;
      default:
        return F1Img;
    }
  }

  if (activePanel) {
    return (
      <div className="relative py-1 text-lg">
        <button
          onClick={() => {
            setOpen(!open);
          }}
          className="flex items-center"
        >
          <div className="leading-tight">
            {t('Select yours')} {getTitle(activePanel)}
          </div>
          <FiChevronDown className="h-4 w-4 ml-0 mt-1" />
        </button>
        {open && (
          <div
            ref={ref}
            className="absolute w-96 top-0 mt-8 bg-white border border-gray-400 z-75"
          >
            {Object.values(ActivePanel)
              .filter(panel => filterPanel(panel))
              .map(panel => (
                <button
                  key={panel}
                  onClick={() => {
                    switchPanel(panel);
                  }}
                  className="flex items-center py-3 px-3 hover:bg-gray-200 w-full"
                >
                  <Img src={getImg(panel)} alt="doors" className="h-12 w-12" />
                  <div className="ml-2">
                    {t('Switch to')} {getTitle(panel)}
                  </div>
                </button>
              ))}
          </div>
        )}
      </div>
    );
  } else return null;
};

export default PanelSwitcher;
